import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useAppStore } from "@hooks/useAppStore";
import {
	getReportSkills,
	isReportSummativeTest,
} from "@memorang/helpers/report";
import type { ReportDetails } from "@memorang/types";
import { trackCustomEvent } from "analytics";
import { useCallback, useMemo } from "react";

export type ReportEventParams = Record<string, string | number | undefined>;

export type TrackReportEvent = (
	eventName: string,
	params?: ReportEventParams,
) => void;

type Options = {
	reportDetails?: ReportDetails;
	view?: string;
};

const getReportSkillTitles = (reportDetails: ReportDetails) => {
	const topics = reportDetails.topicsFilterContext?.includes("REPORTS")
		? (reportDetails.topics ?? []).map((topic) => topic.toLowerCase())
		: [];
	return getReportSkills(reportDetails.tagDetails, topics).map(
		(tag) => tag.title,
	);
};

const getReportScore = (
	reportDetails: ReportDetails,
	maxScaledScore: number,
) => ({
	score: reportDetails.numTotalPoints
		? Number(reportDetails.numPointsEarned ?? 0)
		: Number(reportDetails.score ?? 0),
	maxScore: reportDetails.numTotalPoints || maxScaledScore,
});

/**
 * Stamps every session report event with the report's context so downstream
 * analytics can segment by score, assessment type, collection, exam and skill.
 * `examId`/`examName` are omitted here — `setBaseEventProperties` already adds
 * them to every custom event.
 */
export const useReportAnalytics = ({
	reportDetails,
	view = "session_report",
}: Options) => {
	const {
		currentExamContext: { studyPackCollectionId },
	} = useExamContext();
	const maxScaledScore = useAppStore((state) => state.app.maxScaledScore);

	const baseParams = useMemo<ReportEventParams>(() => {
		if (!reportDetails) {
			return { view };
		}

		const skills = getReportSkillTitles(reportDetails);

		return {
			view,
			sessionId: Number(reportDetails.id),
			sessionNumericId: reportDetails.numericId,
			...getReportScore(reportDetails, maxScaledScore),
			timeElapsed: reportDetails.timeElapsed,
			assessmentType: reportDetails.reportSessionType,
			assessmentSubType: reportDetails.subType,
			isSummativeTest: isReportSummativeTest(reportDetails, {
				studyPackCollectionId,
			})
				? 1
				: 0,
			isDiagnostic: reportDetails.examName.includes("Diagnostic") ? 1 : 0,
			collectionId: studyPackCollectionId,
			numItems: reportDetails.numQuestions,
			...(skills.length > 0 ? { skills: skills.join(", ") } : {}),
		};
	}, [reportDetails, view, maxScaledScore, studyPackCollectionId]);

	const trackReportEvent = useCallback<TrackReportEvent>(
		(eventName, params) => {
			trackCustomEvent({ eventName, ...baseParams, ...params });
		},
		[baseParams],
	);

	return { trackReportEvent };
};
